import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, ShoppingCart, Package, MessageCircle, Search, LogOut } from 'lucide-react';
import { useCartStore } from '@/store/cart';
import { useChatStore } from '@/store/chat';
import { useAuthStore } from '@/store/auth';
import { useOrdersStore } from '@/store/orders';
import { useToastStore } from '@/store/toasts';
import { ToastContainer } from '@/components/ui/Toast';
import { OfflineBanner } from '@/components/OfflineBanner';

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const cart = useCartStore((state) => state.cart);
  const threads = useChatStore((state) => state.threads);
  const activeOrders = useOrdersStore((state) => state.activeOrders);
  const { logout } = useAuthStore();
  const unreadCount = threads.reduce((sum, thread) => sum + thread.unread_count, 0);
  
  const { toasts, removeToast } = useToastStore();

  const navItems = [
    { path: '/', icon: Home, label: 'Home', hasBadge: false },
    { path: '/search', icon: Search, label: 'Search', hasBadge: false },
    { path: '/cart', icon: ShoppingCart, label: 'Cart', hasBadge: (cart?.total_items || 0) > 0 },
    { path: '/orders', icon: Package, label: 'Orders', hasBadge: activeOrders.length > 0 },
    { path: '/chat', icon: MessageCircle, label: 'Chat', hasBadge: unreadCount > 0 },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Offline Banner */}
      <OfflineBanner />

      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 md:ml-64">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <picture>
                <source srcSet="/frog_logo.webp" type="image/webp" />
                <img 
                  src="/frog_logo.png" 
                  alt="FrogBasket" 
                  className="h-10 w-10 object-contain"
                  loading="eager"
                />
              </picture>
              <h1 className="text-2xl font-bold text-[#0D4D2E]">
                FrogBasket
              </h1>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full px-4 py-6 md:ml-64 pb-20">
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className="bg-white border-t border-neutral-200 md:hidden fixed bottom-0 left-0 right-0 z-50">
        <div className="flex justify-around items-center h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full relative ${
                  isActive ? 'text-[#0D4D2E]' : 'text-neutral-500'
                }`}
              >
                <div className="relative">
                  <Icon className="w-6 h-6" />
                  {/* Red dot only - no counter */}
                  {item.hasBadge && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
                  )}
                </div>
                <span className="text-xs mt-1">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Sidebar Navigation (Desktop) */}
      <nav className="hidden md:flex flex-col fixed left-0 top-20 bottom-0 w-64 bg-white border-r border-neutral-200 p-4">
        <ul className="space-y-2 flex-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors relative ${
                    isActive
                      ? 'bg-[#0D4D2E]/10 text-[#0D4D2E]'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {/* Red dot only - no counter */}
                  {item.hasBadge && (
                    <span className="ml-auto w-2.5 h-2.5 bg-red-500 rounded-full" />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
        
        {/* Sign Out Button (Desktop Sidebar) */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors mt-4 border-t border-neutral-200 pt-4"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Sign Out</span>
        </button>
      </nav>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}
